import { useState } from "react";
import { Outlet } from "react-router";
import { Sidebar } from "@/components/layout/sidebar";
import { Header } from "@/components/layout/header";
import { useAuthUser } from "@/components/providers/auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { useCurrentRole } from "@/lib/hooks/use-current-role";
import { ConnectedQuotaBanner } from "@/components/usage";
import { LocationScopedErrorBoundary } from "@/components/providers/route-error-boundary";
import { PageScrollerContext } from "@/lib/hooks/use-page-scroller";
import { useSiteScopeContinuity } from "@/lib/hooks/use-site-scope";
import { SiteScopeNotice } from "@/components/layout/site-scope-notice";

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const { user } = useAuthUser();
  const { profile } = useProvisioning();
  const { role, isResolved } = useCurrentRole();
  const scope = useSiteScopeContinuity();

  const userInfo = {
    email: profile?.user.email,
    name: profile?.user.name ?? undefined,
    picture: user?.picture,
  };

  return (
    // `data-current-role` appears once provisioning has answered. A returning
    // user's page renders before then, as `viewer`, so "this control is not
    // offered" is only an observation about the role once it is set - which
    // is what the E2E role tests wait for (task 181).
    //
    // The layout is the window's height, less the demo or operator-view
    // banner above it, and only `<main>` scrolls: the sidebar and header stay
    // put, even through macOS's rubber-band bounce, which carried a sticky
    // sidebar along with the window.
    <div
      className="flex h-[calc(100dvh-var(--app-banner-height,0px))] overflow-hidden"
      data-current-role={isResolved ? role : undefined}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {/* min-w-0: wide content (a table) scrolls in its own box rather
          than squeezing the sidebar. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Header onMenuClick={() => setSidebarOpen(true)} user={userInfo} />
        {/* overscroll-contain: a fast scroll's momentum stops here rather
            than passing to the page, which would bounce as a whole.
            relative: an absolute element inside with no positioned ancestor
            of its own, such as a screen-reader label, is placed against
            this box and scrolls with it; placed against the page, one below
            the fold made the window itself scroll (task 464). */}
        <main
          ref={setScroller}
          data-page-scroller=""
          className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
          <div className="space-y-3 px-4 pt-4 lg:px-6">
            <ConnectedQuotaBanner />
            <SiteScopeNotice
              narrowedTo={scope.narrowedTo}
              onDismiss={scope.dismiss}
            />
          </div>
          <PageScrollerContext.Provider value={scroller}>
            <LocationScopedErrorBoundary>
              <Outlet />
            </LocationScopedErrorBoundary>
          </PageScrollerContext.Provider>
        </main>
      </div>
    </div>
  );
}
